import type { ChangeEvent, ReactNode } from 'react';
import type { Margens, Timbrado } from '../documento';
import { MarcaDoTimbrado, textoDoRodape } from './pagina';
import { BotaoNu } from './comum';
// O editor do timbrado também vive fora do editor (Configurações → Papel timbrado).
import '../editor-forense.css';

export type ModoDoTimbrado = Timbrado['modo'];

const IMAGEM = 'image/png,image/jpeg';

/** Um timbrado em branco, no primeiro modo que a tela oferece. */
export function novoTimbrado(modo: ModoDoTimbrado = 'dados'): Timbrado {
  return { id: `t${Date.now().toString(36)}`, rotulo: 'Novo timbrado', modo, nome: '', oab: '', endereco: '', contato: '' };
}

/**
 * O editor de UM timbrado — o cabeçalho e o rodapé, cada um em texto ou em
 * imagem, e a folha em miniatura. É o mesmo no painel do timbrado (o editor de
 * peças) e em Configurações → Papel timbrado: cada tela só põe a moldura
 * (`antes`, `depois`) e decide o que fazer com o timbrado editado.
 *
 * `modos`: com o app, só `texto` e `imagem` (o servidor guarda texto, logotipo e
 * faixas; os "dados do escritório" voltariam como texto) — e aí não há o que
 * escolher. Sem o app, ainda se escolhe entre os dados do escritório e o resto.
 * `t` nulo: sem timbrado (a folha sai em branco e não há campos).
 */
export function EditorDeTimbrado({ t, aoMudar, modos = ['dados', 'texto', 'imagem'], antes, depois, marcaDagua }: {
  t: Timbrado | null;
  aoMudar: (t: Timbrado) => void;
  modos?: ModoDoTimbrado[];
  antes?: ReactNode;
  depois?: ReactNode;
  /** A marca d'água que se edita (Configurações); sem ela, a do timbrado. */
  marcaDagua?: string;
}) {
  return (
    <div className="cm-ef-painel">
      <div className="cm-ef-painel-campos">
        {antes}
        {t?.somenteLeitura && (
          <section className="cm-ef-secao">
            <p className="cm-ef-mini-leg">{t.aviso ?? 'Este timbrado é cuidado em outro lugar do sistema; aqui você só o escolhe.'}</p>
          </section>
        )}
        {t && !t.somenteLeitura && <CamposDoTimbrado t={t} aoMudar={aoMudar} modos={modos} />}
        {depois}
      </div>
      <aside className="cm-ef-diagrama" aria-label="Como fica">
        <h3>Como fica</h3>
        <PreviaDaFolha t={t} marcaDagua={marcaDagua ?? t?.marcaDagua} />
        <p className="cm-ef-mini-leg">O número de página e o de folhas continuam por cima do timbrado.</p>
      </aside>
    </div>
  );
}

/** Com alguma faixa, o timbrado é do modo imagem; sem nenhuma, texto (os "dados" ficam como estão). */
function comModo(t: Timbrado): Timbrado {
  if (t.modo === 'dados') return t;
  return { ...t, modo: t.imagemCabecalho || t.imagemRodape ? 'imagem' : 'texto' };
}

/**
 * Os campos: o cabeçalho (logotipo e texto, ou uma imagem no lugar) e o rodapé
 * (texto, ou uma imagem no lugar), cada um por si — qualquer combinação. O
 * modo sai das escolhas (`comModo`); só o editor sem o app ainda escolhe os
 * "dados do escritório".
 */
function CamposDoTimbrado({ t, aoMudar: mudar, modos }: { t: Timbrado; aoMudar: (t: Timbrado) => void; modos: ModoDoTimbrado[] }) {
  const aoMudar = (x: Timbrado) => mudar(comModo(x));
  const lerArquivo = (campo: 'logotipo' | 'imagemCabecalho' | 'imagemRodape') => (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const r = new FileReader();
    r.onload = () => aoMudar({ ...t, [campo]: String(r.result) });
    r.readAsDataURL(f);
  };
  const campo = (rotulo: string, k: 'nome' | 'oab' | 'endereco' | 'contato') => (
    <label className="cm-ef-rot">{rotulo}<input className="cm-ef-campo" value={t[k] ?? ''} onChange={(e) => aoMudar({ ...t, [k]: e.target.value })} /></label>
  );
  const logotipo = (
    <>
      <span className="cm-ef-mini-leg">Logotipo, à esquerda do texto</span>
      <div className="cm-ef-linha-bt">
        {t.logotipo && <img src={t.logotipo} alt="Logotipo" className="cm-ef-borda-img" />}
        <label className="cm-ef-arquivo">{t.logotipo ? 'Trocar o logotipo' : 'Enviar logotipo'}<input type="file" accept={IMAGEM} onChange={lerArquivo('logotipo')} /></label>
        {t.logotipo && <BotaoNu className="cm-ef-arquivo" onClick={() => aoMudar({ ...t, logotipo: undefined })}>Tirar o logotipo</BotaoNu>}
      </div>
    </>
  );
  const usarImagem = (img: 'imagemCabecalho' | 'imagemRodape') => (
    <label className="cm-ef-arquivo">Usar uma imagem no lugar<input type="file" accept={IMAGEM} onChange={lerArquivo(img)} /></label>
  );
  // A borda em imagem: a faixa pronta (pela gráfica ou em Word), na largura do texto.
  const faixa = (img: 'imagemCabecalho' | 'imagemRodape', rotulo: string) => (
    <>
      <img src={t[img]} alt={`Imagem do ${rotulo}`} className="cm-ef-borda-img" />
      <div className="cm-ef-linha-bt">
        <label className="cm-ef-arquivo">Trocar a imagem<input type="file" accept={IMAGEM} onChange={lerArquivo(img)} /></label>
        <BotaoNu className="cm-ef-arquivo" onClick={() => aoMudar({ ...t, [img]: undefined })}>Usar texto no lugar</BotaoNu>
      </div>
    </>
  );
  const grupo = (titulo: string, conteudo: ReactNode) => (
    <fieldset className="cm-ef-tb-grupo">
      <legend className="cm-ef-rot">{titulo}</legend>
      {/* O flex mora numa div: no próprio fieldset o navegador o aplica mal. */}
      <div className="cm-ef-borda">{conteudo}</div>
    </fieldset>
  );

  if (t.modo === 'dados') {
    return (
      <section className="cm-ef-secao">
        <SeletorDeDados t={t} aoMudar={aoMudar} modos={modos} />
        {grupo('Cabeçalho', <>{logotipo}<div className="cm-ef-linhas">{campo('Nome do escritório', 'nome')}{campo('OAB', 'oab')}</div></>)}
        {grupo('Rodapé', <div className="cm-ef-linhas">{campo('Endereço', 'endereco')}{campo('Telefone e e-mail', 'contato')}</div>)}
      </section>
    );
  }
  return (
    <section className="cm-ef-secao">
      <SeletorDeDados t={t} aoMudar={aoMudar} modos={modos} />
      {grupo('Cabeçalho', t.imagemCabecalho ? (
        <>
          {faixa('imagemCabecalho', 'cabeçalho')}
          {t.logotipo && <p className="cm-ef-mini-leg">O logotipo fica guardado, mas não aparece enquanto o cabeçalho for uma imagem.</p>}
        </>
      ) : (
        <>
          {logotipo}
          <label className="cm-ef-rot">Texto do cabeçalho (uma informação por linha; a primeira sai em negrito)
            <textarea className="cm-ef-campo cm-ef-campo-area" rows={3} value={t.cabecalhoTexto ?? ''} onChange={(e) => aoMudar({ ...t, cabecalhoTexto: e.target.value })}
              placeholder={'Santos & Lima Advogados\nOAB/AL 1.234\nRua do Comércio, 100 – Maceió/AL'} />
          </label>
          {usarImagem('imagemCabecalho')}
        </>
      ))}
      {grupo('Rodapé', t.imagemRodape ? faixa('imagemRodape', 'rodapé') : (
        <>
          <label className="cm-ef-rot">Texto do rodapé
            <textarea className="cm-ef-campo cm-ef-campo-area" rows={3} value={t.rodapeTexto ?? ''} onChange={(e) => aoMudar({ ...t, rodapeTexto: e.target.value })}
              placeholder="contato@escritorio.adv.br – (82) 3000-0000" />
          </label>
          {usarImagem('imagemRodape')}
        </>
      ))}
    </section>
  );
}

/** Só no editor sem o app: os "dados do escritório" ou o texto livre (com imagens, se quiser). */
function SeletorDeDados({ t, aoMudar, modos }: { t: Timbrado; aoMudar: (t: Timbrado) => void; modos: ModoDoTimbrado[] }) {
  if (!modos.includes('dados')) return null;
  return (
    <div className="cm-ef-seg cm-ef-seg-txt" role="group" aria-label="Modo">
      <BotaoNu aria-pressed={t.modo === 'dados'} onClick={() => aoMudar({ ...t, modo: 'dados' })}>Com os dados do escritório</BotaoNu>
      <BotaoNu aria-pressed={t.modo !== 'dados'} onClick={() => aoMudar({ ...t, modo: 'texto' })}>Com texto ou imagens</BotaoNu>
    </div>
  );
}

// Linhas de texto da folha em miniatura: a 1ª e a 4ª abrem parágrafo.
const LINHAS_DA_PREVIA = [100, 100, 80, 100, 100, 60];

/**
 * A folha em miniatura ("Como fica"): o timbrado no alto e no pé, linhas de
 * texto e o número de página. Com `margens` e `recuo` (cm), desenha a página
 * medida de um arquivo; sem eles, a de sempre.
 */
export function PreviaDaFolha({ t, numerada = true, margens, recuo = 0, marcaDagua }: {
  t: Timbrado | null;
  marcaDagua?: string;
  numerada?: boolean;
  margens?: Partial<Margens>;
  recuo?: number;
}) {
  // A miniatura tem a largura do A4 (21 cm); o texto, uns 16 cm.
  const lado = (cm?: number) => (cm === undefined ? undefined : `${((cm / 21) * 100).toFixed(1)}%`);
  const abre = `${((recuo / 16) * 100).toFixed(1)}%`;
  return (
    <div className="cm-ef-prev-timbre" style={{ paddingLeft: lado(margens?.esquerda), paddingRight: lado(margens?.direita) }}>
      {marcaDagua?.trim() && <span className="cm-ef-pt-marca" aria-label={`Marca d’água: ${marcaDagua.trim()}`}>{marcaDagua.trim()}</span>}
      <div className="cm-ef-pt-cab">
        {!t ? <span /> : t.modo === 'imagem' && t.imagemCabecalho ? <img src={t.imagemCabecalho} alt="" style={{ width: '100%' }} /> : <MarcaDoTimbrado t={t} />}
      </div>
      <div className="cm-ef-pt-corpo">
        {LINHAS_DA_PREVIA.map((largura, i) => (
          <i key={i} style={i === 0 || i === 3 ? { marginLeft: abre, width: `calc(${largura}% - ${abre})` } : { width: `${largura}%` }} />
        ))}
      </div>
      <div className="cm-ef-pt-rod">
        {!t ? <span /> : t.modo === 'imagem' && t.imagemRodape ? <img src={t.imagemRodape} alt="" style={{ width: '100%' }} /> : <span className="cm-ef-tb-texto">{textoDoRodape(t)}</span>}
        {numerada && <span className="cm-ef-pt-num">Página 1 de 2</span>}
      </div>
    </div>
  );
}
